import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import config from "../../config";

export const useUserData = () => {
  const currentUser = AuthStore((state) => state.currentUser);
  const hasHydrated = AuthStore((state) => state._hasHydrated);
  const studentProfile = StudentStore((state) => state.student);

  const fallbackUser = {
    name: 'Student',
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=student',
    totalPoints: 0,
    tasksCompleted: 0,
    streakDays: 0,
    level: 1,
    grade: 'Grade 10',
    email: 'student@example.com',
    joinDate: new Date(),
    rank: 0,
    preferences: {
      notifications: true,
      soundEffects: false,
      theme: 'light',
      language: 'en',
      timezone: 'Africa/Lagos',
      notificationSettings: { email: true, push: true, sms: false },
    },
  };

  const user = {
    ...fallbackUser,
    ...(currentUser ?? {}),
    ...(studentProfile ? {
      name: `${studentProfile.firstName} ${studentProfile.lastName}`.trim() || currentUser?.name || fallbackUser.name,
      email: studentProfile.email || currentUser?.email || fallbackUser.email,
    } : {}),
  };

  const isProto = config.appMode === "prototype";

  // Academic performance: map from real academic results, or empty
  const rawResults = studentProfile?.academicResults as Array<{ subject?: string; score?: number; grade?: string }> | undefined;
  const academicPerformance = isProto
    ? {
        subjects: [
          { name: 'Mathematics', grade: 'A', progress: 85 },
          { name: 'English', grade: 'B+', progress: 78 },
          { name: 'Science', grade: 'A-', progress: 82 },
          { name: 'History', grade: 'B', progress: 75 },
          { name: 'Art', grade: 'A', progress: 90 },
        ],
        overallAverage: 82,
        attendanceRate: 95,
        studyHours: 25,
      }
    : rawResults && rawResults.length > 0
      ? {
          subjects: rawResults.map(r => ({
            name: r.subject ?? 'Unknown',
            grade: r.grade ?? '-',
            progress: typeof r.score === 'number' ? Math.min(100, r.score) : 0,
          })),
          overallAverage: Math.round(
            rawResults.filter(r => typeof r.score === 'number')
              .reduce((sum, r) => sum + (r.score as number), 0) /
            Math.max(1, rawResults.filter(r => typeof r.score === 'number').length)
          ),
          attendanceRate: 0,
          studyHours: studentProfile?.engagementData?.sessionDuration ?? 0,
        }
      : { subjects: [], overallAverage: 0, attendanceRate: 0, studyHours: 0 };

  // Career paths: from careerInterests strings (real mode) or empty
  const careerPaths = isProto
    ? [
        { id: '1', title: 'Software Engineering', match: 95, description: 'High demand career with great growth potential' },
        { id: '2', title: 'Data Science', match: 88, description: 'Analyze data to drive business decisions' },
        { id: '3', title: 'Cybersecurity', match: 82, description: 'Protect systems and data from threats' },
        { id: '4', title: 'UX Design', match: 75, description: 'Design user-friendly digital experiences' },
      ]
    : (studentProfile?.careerInterests ?? []).map((interest, i) => ({
        id: String(i),
        title: interest,
        match: 80,
        description: '',
      }));

  // Goals and achievements are not in the StudentStore shape yet — return empty for real users
  const goals = isProto
    ? [
        { id: '1', title: 'Complete Algebra Course', deadline: '2024-12-31', progress: 60 },
        { id: '2', title: 'Read 5 Books', deadline: '2024-11-30', progress: 80 },
        { id: '3', title: 'Improve Writing Skills', deadline: '2024-12-15', progress: 40 },
      ]
    : [];

  const achievements = isProto
    ? [
        { id: '1', title: 'Perfect Attendance', description: '30 days streak', date: '2024-10-15' },
        { id: '2', title: 'Math Master', description: 'Scored 100% in Algebra', date: '2024-10-10' },
        { id: '3', title: 'Fast Learner', description: 'Completed 5 courses in a week', date: '2024-10-05' },
        { id: '4', title: 'Team Player', description: 'Helped 10 classmates', date: '2024-09-28' },
      ]
    : [];

  const weeklyProgress = isProto
    ? [
        { day: 'Mon', hours: 3 }, { day: 'Tue', hours: 4 }, { day: 'Wed', hours: 2 },
        { day: 'Thu', hours: 5 }, { day: 'Fri', hours: 3 }, { day: 'Sat', hours: 2 }, { day: 'Sun', hours: 1 },
      ]
    : [];

  return {
    user,
    goals,
    achievements,
    weeklyProgress,
    academicPerformance,
    careerPaths,
    isLoading: !hasHydrated,
  };
};